// team-filters.ts — status-effect predicates + ability target/team-filter logic, carved out of template.js
// (roadmap T4.4, 2026-06-16). Byte-faithful port: the only edits versus the template.js originals are `: any`
// param annotations and `any`-typed DOM-query locals — logic is identical. None of these reference the giant
// VISUAL_STATUS_EFFECTS const (it stays in template.js); isOpposingTeamPair reads the staying `teamTypes`
// const as a global. All nine are window-attached at the bottom for the cross-bundle consumers.
//
// Team source (2026-06-20): the team-rule prune/predicate resolve a creature's team from the JSON
// encounterArr CACHE (window.FateTide.encounterCache — the WS-updated source of truth),
// keyed by encounter_creature_id. NOT from any data-* attribute. History of why:
//   • `data-team` on tokens was RETIRED 2026-06-12 (team moved into the blob). team-filters.ts was carved
//     from template.js 2026-06-16 as a faithful port and reproduced the now-dead `el.dataset.team` reads →
//     casterTeam/targetTeam ALWAYS '' → isOpposingTeamPair() ALWAYS false → every
//     effect_only_affects_opposing_team_type ability pruned ALL its targets, emptying the target_id payload → server
//     "This <ability> has a range and requires a target" on a valid, in-range, opposing target.
//   • A DOM-token / button-data fallback is intentionally NOT used: data-* is a stale render-time snapshot;
//     the encounterArr cache is the live truth (the user's directive). `teamFromCacheById` reads it by id,
//     working even when the caster has no on-map token (e.g. cast while not in combat) as long as the
//     encounter's FULL blob is cached.
// Robustness: FAIL OPEN — a target is removed ONLY when BOTH teams resolve to a real friend/enemy UUID AND
// the pair genuinely violates the flag. Unknown team → keep the selection and let the server
// (check_opposing_team in is_target_valid_for_effect) adjudicate. So a resolution gap can never again
// silently delete a valid target.
// token-data.ts is safe to bundle here (its dependency rule: imports ONLY from ../shared/array-types).
import { teamFromCacheById } from '../encounter-map/token-data';

/** A team UUID that participates in opposing/same checks (friend or enemy; neutral/'' do not). */
function isFriendOrEnemyTeam(team: unknown): boolean {
    const t = String(team || '').trim().toUpperCase();
    return t === teamTypes.TEAM_FRIEND.toUpperCase() || t === teamTypes.TEAM_ENEMY.toUpperCase();
}

/** encounter_creature_id off a token element (IDENTITY attr — the one thing tokens legitimately carry). */
function creatureIdOf(el: Element | null | undefined): string {
    return String(el?.getAttribute('data-encounter_creature_id') ?? '').trim();
}

/**
 * Generic check: does the entity have a status effect matching any of the given UUID(s)?
 * NOW SUPPORTS BOTH normal layered statuses AND status_effect_permanent (flat UUID array)
 * Handles single string or array of strings.
 */
function hasStatusEffect(entity: unknown, uuids: unknown): boolean {
    if (!entity) return false;

    const targetUuids = Array.isArray(uuids) ? uuids : [uuids];

    // 1. Normal layered statuses (what you already had)
    const normalStatuses = ftCreatureStatuses(entity, 'target');

    const hasNormal = normalStatuses.some((eff: unknown) => {
        if (statusEffectInstanceIsCampaignPaused(eff)) return false;
        const effId = ftItemsCol(eff, 'item_id') ?? '';
        return targetUuids.includes(effId);
    });

    if (hasNormal) return true;

    // 2. Permanent statuses — resolved UUIDs via the bridge-aware reader
    // (normalized PC casters carry them in computed.creature.abilities, monsters
    // on root/item_layer_types; ftStatusEffectPermanentUuids covers all shapes).
    const permStatuses = ftStatusEffectPermanentUuids(entity);

    return permStatuses.some((id: string) => {
        return checkIfValidUUID(id) && targetUuids.includes(id);
    });
}

/**
 * Layered status instances only (encounter `status_effect` rows), ignoring
 * `status_effect_permanent`. Used for map visuals that must reflect an active
 * condition (e.g. Immobile) rather than template/permanent lines or zero speeds.
 */
function hasLayeredStatusEffect(entity: unknown, uuids: unknown): boolean {
    if (!entity) return false;
    const targetUuids = Array.isArray(uuids) ? uuids : [uuids];
    const normalStatuses = ftCreatureStatuses(entity, 'target');
    return normalStatuses.some((eff: unknown) => {
        if (statusEffectInstanceIsCampaignPaused(eff)) return false;
        const effId = ftItemsCol(eff, 'item_id') ?? '';
        return targetUuids.includes(effId);
    });
}

/** Mirrors PHP check_opposing_team: both friend/enemy, then different teams. */
function isOpposingTeamPair(casterTeamStr: unknown, targetTeamStr: unknown): boolean {
    const f = teamTypes.TEAM_FRIEND.toUpperCase();
    const e = teamTypes.TEAM_ENEMY.toUpperCase();
    const c = String(casterTeamStr || '').trim().toUpperCase();
    const t = String(targetTeamStr || '').trim().toUpperCase();
    if (c !== f && c !== e) return false;
    if (t !== f && t !== e) return false;
    return c !== t;
}

interface AbilityTeamFlags { sameTeamOnly: boolean; opposingTeamOnly: boolean; }

function abilityTeamFlagsFromButtonDataset(btn: Element | null | undefined): AbilityTeamFlags {
    if (!btn) return { sameTeamOnly: false, opposingTeamOnly: false };
    return {
        sameTeamOnly: btn.getAttribute('data-effect_only_affects_same_team_type') === '1',
        opposingTeamOnly: btn.getAttribute('data-effect_only_affects_opposing_team_type') === '1',
    };
}

function healingInventoryFlagsFromButtonDataset(btn: Element | null | undefined): { canHealInventory: boolean; onlyHealInventory: boolean } {
    if (!btn) return { canHealInventory: false, onlyHealInventory: false };
    return {
        canHealInventory: btn.getAttribute('data-effect_can_heal_inventory') === '1',
        onlyHealInventory: btn.getAttribute('data-effect_can_only_heal_inventory') === '1',
    };
}

// NOTE: the manual-selection team prune (queryAbilityButtonByItemId / pruneTargetSelectionForTeamFlags /
// pruneTargetsForAbilityItemId) was REMOVED 2026-06-20. Using an ability must NOT clear the user's
// selected targets — that prune emptied the target_id payload and produced a false "requires a target" warning. The
// server (is_target_valid_for_effect / check_opposing_team) enforces team rules at effect time. Only the
// AOE-coverage predicate below survives; it filters which creatures an AOE TEMPLATE covers, and never
// touches a manual single-target pick.

function creaturePassesTeamEffectFilters(casterEl: Element | null, creatureEl: Element | null, flags: AbilityTeamFlags | null | undefined): boolean {
    if (!flags || (!flags.sameTeamOnly && !flags.opposingTeamOnly)) return true;
    if (!creatureEl?.classList?.contains('encounter-creature')) return false;
    // Teams from the encounterArr cache by id (source of truth), not data-* on the elements.
    const casterTeam = teamFromCacheById(creatureIdOf(casterEl));
    const targetTeam = teamFromCacheById(creatureIdOf(creatureEl));
    // FAIL OPEN on unknown team (same rationale as pruneTargetSelectionForTeamFlags): if we can't
    // classify the pair, let it pass rather than filter out a possibly-valid target.
    if (!isFriendOrEnemyTeam(casterTeam) || !isFriendOrEnemyTeam(targetTeam)) return true;
    const opposing = isOpposingTeamPair(casterTeam, targetTeam);
    if (flags.sameTeamOnly && opposing) return false;
    if (flags.opposingTeamOnly && !opposing) return false;
    return true;
}

/**
 * Instant client-side team pre-warning for a SINGLE selected creature target — the UX mirror of the
 * server pre-flight gate (ability_target_gate_failure / is_target_valid_for_effect / the janus.php
 * aggregate). An `effect_only_affects_opposing_team_type` ability aimed at a non-opposing creature, or
 * `effect_only_affects_same_team_type` aimed at an opposing one, is a guaranteed no-op the server would
 * only warn on after a wasted round-trip. Returns the warning string, or null to send normally.
 *
 * FAIL-OPEN (same rationale as creaturePassesTeamEffectFilters): any unresolved piece — not exactly one
 * creature target, ability blob not cached, aura / persistent_aoe (those legitimately reach a same-team
 * primary via aura_special / zone-entry), or a caster/target team that isn't a real friend/enemy UUID —
 * returns null so the SERVER stays the authority and a valid use is never blocked. Reads the ability's
 * layers from the cached blob (ftFindFullNestedItem), not the render-time button snapshot, so it can tell
 * an ABSENT aoe layer from a "0" one; teams come from the encounterArr cache (teamFromCacheById), the WS
 * source of truth. Callers that already resolved the caster id pass it; else pass '' and it fails open.
 */
function abilityTeamGateWarning(itemId: unknown, casterId: unknown, targetIds: unknown): string | null {
    const ids = Array.isArray(targetIds)
        ? targetIds.map((t: unknown) => String(t ?? '').trim()).filter(Boolean)
        : (targetIds ? [String(targetIds).trim()] : []);
    if (ids.length !== 1) return null; // single confirmed target only — AOE/multi stay server-adjudicated
    const targetId = ids[0];
    const casterIdStr = String(casterId ?? '').trim();
    if (!checkIfValidUUID(targetId) || !checkIfValidUUID(casterIdStr)) return null;

    const finder = (window as unknown as { ftFindFullNestedItem?: (id: string) => unknown }).ftFindFullNestedItem;
    const ability = (typeof finder === 'function') ? finder(String(itemId ?? '')) : null;
    if (!ability) return null; // fail open — cannot prove ineligibility

    const truthy = (v: unknown): boolean => { const s = String(v ?? '').trim(); return s !== '' && s !== '0'; };
    const present = (v: unknown): boolean => v !== null && v !== undefined;

    const opposingOnly = truthy(ftLayerValue(ability, 'effect_only_affects_opposing_team_type'));
    const sameOnly = truthy(ftLayerValue(ability, 'effect_only_affects_same_team_type'));
    if (!opposingOnly && !sameOnly) return null;

    // Auras (check_item_arr_aura_effect: all four aoe layers PRESENT) + active persistent_aoe legitimately
    // reach a same-team primary — never block them (parity with the server gate's exclusions).
    const isAura = present(ftLayerValue(ability, 'aoe_type')) && present(ftLayerValue(ability, 'aoe_distance'))
        && present(ftLayerValue(ability, 'persistent_aoe')) && present(ftLayerValue(ability, 'aoe_follows_target'));
    if (isAura || truthy(ftLayerValue(ability, 'persistent_aoe'))) return null;

    const casterTeam = teamFromCacheById(casterIdStr);
    const targetTeam = teamFromCacheById(targetId);
    // Fail open on unknown/neutral team (matches the server's check_opposing_team semantics enough that the
    // back-end still warns on a neutral target; the client just declines to pre-empt that case).
    if (!isFriendOrEnemyTeam(casterTeam) || !isFriendOrEnemyTeam(targetTeam)) return null;

    const opposing = isOpposingTeamPair(casterTeam, targetTeam);
    const name = String(ftItemsCol(ability, 'item_name') || 'This ability');
    if (opposingOnly && !opposing) {
        return `${name} only affects opposing-team creatures — this target is not on an opposing team.`;
    }
    if (sameOnly && opposing) {
        return `${name} only affects same-team creatures — this target is on an opposing team.`;
    }
    return null;
}

// ===========================================================================================
// Window attachment — the public surface these carved functions expose to cross-bundle consumers
// (creature-render / inventory for the status predicates; ft3d-oa-threat for isOpposingTeamPair; encounter-map
// AOE* for the team-flag readers + creaturePassesTeamEffectFilters). The manual-selection prune trio
// (queryAbilityButtonByItemId / pruneTargetSelectionForTeamFlags / pruneTargetsForAbilityItemId) was removed
// 2026-06-20 — see the note above creaturePassesTeamEffectFilters. All reached at runtime.
// ===========================================================================================
declare global {
    interface Window {
        hasStatusEffect: typeof hasStatusEffect;
        hasLayeredStatusEffect: typeof hasLayeredStatusEffect;
        isOpposingTeamPair: typeof isOpposingTeamPair;
        abilityTeamFlagsFromButtonDataset: typeof abilityTeamFlagsFromButtonDataset;
        healingInventoryFlagsFromButtonDataset: typeof healingInventoryFlagsFromButtonDataset;
        creaturePassesTeamEffectFilters: typeof creaturePassesTeamEffectFilters;
        abilityTeamGateWarning: typeof abilityTeamGateWarning;
        teamFromCacheById: typeof teamFromCacheById;
    }
}

Object.assign(window, {
    hasStatusEffect,
    hasLayeredStatusEffect,
    isOpposingTeamPair,
    abilityTeamFlagsFromButtonDataset,
    healingInventoryFlagsFromButtonDataset,
    creaturePassesTeamEffectFilters,
    abilityTeamGateWarning,
    // Exposed so cross-bundle consumers (info-panel's contextual-actions cluster) can resolve a creature's
    // team from the live encounterArr cache to check_opposing_team a rider, without re-importing the map layer.
    teamFromCacheById,
});

export {};
